import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { renderRichText as renderContentfulRichText } from "gatsby-source-contentful/rich-text"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import {
  RenderMark,
  RenderNode,
  RenderText,
  NodeRenderer
} from "@contentful/rich-text-react-renderer"
import { Typography } from "@eventbrite/marmalade"

// components
import { Alert } from "../../components/Alert/Alert"
import { VimeoVideo } from "../../components/VimeoVideo/VimeoVideo"

// styles
import * as styles from "./GatsbyImage.module.scss"

export interface RenderRichTextOptions {
  renderNode?: RenderNode
  renderMark?: RenderMark
  renderText?: RenderText
  references?: any
}

export interface VideoContent {
  __typename: string
  contentful_id: string
  videoTitle: string
  videoUrl: string
  videoThumbnailUrl: string
  videoCaption: {
    videoCaption: string
  }
  videoThumbnailAltText: {
    videoThumbnailAltText: string
  }
  sys: {
    type: string
    id: string
  }
}

export type HelpArticleAlertData = Pick<
  Queries.ContentfulAlert,
  | "contentful_id"
  | "alertShowIcon"
  | "alertDescription"
  | "alertDismissBoolean"
  | "alertTypeReference"
  | "alertCta"
  | "alertHeader"
> & {
  __typename: string
}

const embeddedEntryRenderer: NodeRenderer = (node) => {
  if (node?.data?.target?.__typename === "ContentfulAlert") {
    const alertData = node.data.target as HelpArticleAlertData

    return <Alert alertData={alertData} />
  }

  if (node?.data?.target?.__typename === "ContentfulVideo") {
    const target = node?.data?.target as VideoContent

    return (
      <VimeoVideo
        src={target.videoUrl}
        caption={target?.videoCaption?.videoCaption}
      />
    )
  }

  if (node?.data?.target?.__typename === "ContentfulImage") {
    const image = node?.data?.target?.image
    const { description, mimeType, width, height, url } = image

    if (image) {
      // SVG
      if (mimeType === "image/svg+xml" && url) {
        const svgProps = width && height ? { width, height } : {}

        return (
          <img
            src={url}
            alt={description || ""}
            style={{ maxWidth: "100%" }}
            {...svgProps}
          />
        )
      }

      if (image?.gatsbyImageData) {
        // RASTER IMAGE
        return (
          <GatsbyImage
            className={styles?.gatsbyImageWrapper}
            image={getImage(image.gatsbyImageData)}
            alt={image?.description}
          />
        )
      }
    }
  }

  return null
}

// This code block allows us to specially handle different parts of the rich text editor.
export const contentfulRenderOptions: RenderRichTextOptions = {
  renderNode: {
    [INLINES.EMBEDDED_ENTRY]: embeddedEntryRenderer,
    // Inside of {ContentfulHelpArticle.slug}.tsx there is this code: `export const query = graphql` this query fetches any gatsbyImageData that is available for an asset (if it is an image) and includes it.
    // We can handle all other Contentful Asset types here
    [BLOCKS.EMBEDDED_ENTRY]: embeddedEntryRenderer,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const target = node?.data?.target || {}
      const { gatsbyImageData, description, mimeType, width, height, url } =
        target

      // RASTER IMAGE
      if (gatsbyImageData) {
        return (
          <GatsbyImage
            className={styles?.gatsbyImageWrapper}
            image={getImage(gatsbyImageData)}
            alt={description}
          />
        )
      }
      if (node?.data?.target?.__typename === "ContentfulImage") {
        const gatsbyImageData = node?.data?.target?.image?.gatsbyImageData
        const description = node?.data?.target?.image?.description

        return (
          <GatsbyImage
            className={styles?.gatsbyImageWrapper}
            image={getImage(gatsbyImageData)}
            alt={description}
          />
        )
      }

      // SVG
      if (mimeType === "image/svg+xml" && url) {
        const svgProps = width && height ? { width, height } : {}

        return (
          <img
            src={url}
            alt={description || ""}
            style={{ maxWidth: "100%" }}
            {...svgProps}
          />
        )
      }

      return null
    },
    [BLOCKS.PARAGRAPH]: (node, children) => {
      // @TODO: need to remove this entirely
      return (
        <Typography variant="body-md" as="p">
          {children}
        </Typography>
      )
    }
  },
  references: []
}

export const renderRichText = (
  raw?: string,
  options: RenderRichTextOptions = {},
  debugLog = false
): React.ReactNode => {
  if (!raw || typeof window === "undefined") {
    return null
  }

  const finalOptions = { ...contentfulRenderOptions, ...options }

  if (debugLog) {
    console.log("🚀 ~ DEBUG LOG:", { raw, options, finalOptions })
  }

  return renderContentfulRichText(
    {
      raw: raw,
      references: options.references ? options.references : []
    },
    finalOptions
  )
}

export const customRendererToRemovePTag: RenderRichTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>
  }
}
